import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { SendArToIntacctInvoice } from './send-ar-to-intacct-invoice.model';
import { ToastService } from '../../../shared/toast.service'
import { InvoiceService } from '../../../views/invoice/invoice.service';
import { AccountingService } from '../../../views/accounting/accounting.service';
import { GridDataResult, RowArgs, DataStateChangeEvent, RowClassArgs } from '@progress/kendo-angular-grid';
import { LabelFn, LabelSettings } from '@progress/kendo-angular-progressbar';
import { Subject } from 'rxjs';
import { aggregateBy } from '@progress/kendo-data-query';
import { ArInvoiceType } from '../../../shared/arInvoiceType.model';
import { startWith } from 'rxjs/operators';

@Component({
	selector: 'send-ar-to-intacct',
	templateUrl: './send-ar-to-intacct.component.html'
})
export class SendArToIntacctComponent implements OnInit {
	@Input() arInvType: ArInvoiceType;
	@Input() data: any[];
	@Input() billingPeriod: Date;
	@Input() showSendArPanel: boolean;
	@Output() onClosed = new EventEmitter();

	selectedArInvoices: any[];
	intacctResponse: any[];
	purchaseOrderPaymentStatus: string;
	purchaseOrderState: string;
	sendingRequestToIntacct: boolean;
	lockActions = false;
	processingPeriod: Date;

	public arPreviewData: GridDataResult;
	public arPreviewLoading: boolean;
	public emailContactsLoading = false;
	public arPreviewArTotal: any;
	public baseRentalTotals: any;
	public carhireTotals: any;
	public noWorkTotals: any;
	public scrapTotals: any;
	public arExceedMileageTotals: any;
	public arTankcarEqualizationTotals: any;
	public arInvoicePreviewContent: any;
	public stipLossFmvTotals: any;
	public canSendAr = false;
	public sentToIntactInvoices = 0;
	public sendInvoicesInprogress = false;
	public arInvoiceIds = [];
	public invoicesToSendSource = new Subject<any[]>();
	public arProgressLabel: string;
	public showIntacctResponseSidePanel = false;
	public canClosePannel = true;
	public totalSentToIntacct = 0;
	public totalToSendToIntacct = 0;
	
	public formatter: LabelFn = (value: number): string => {
		return `${value}/${this.selectedArInvoices.length}`;
	}
	public label: LabelSettings = {
		format: this.formatter,
		position: 'center'
	};

	constructor(
		private invoiceService: InvoiceService,
		private accountingService: AccountingService,
		private toastr: ToastService) {
	}

	ngOnInit() {
		if (!this.data) {
			this.selectedArInvoices = new Array<SendArToIntacctInvoice>();
		} 
		else {
			this.selectedArInvoices = this.data.map(x=> {return new SendArToIntacctInvoice(x)});
			switch (this.arInvType)
			{
				case ArInvoiceType.BaseRentalCustomer:
					this.canSendAr = true;
					const baserentalInvs = this.selectedArInvoices.filter(x => !x.isPosted);
					this.baseRentalTotals = aggregateBy(baserentalInvs, [{field: 'baseTotal', aggregate: 'sum'}, 
							{field: 'adjustmentTotal', aggregate: 'sum' }, {field: 'total', aggregate: 'sum' }]);
					break;
				case ArInvoiceType.BaseRentalOwner:
					this.canSendAr = true;
					const baserentalOwnerInvs = this.selectedArInvoices.filter(x => !x.isPosted);
					this.baseRentalTotals = aggregateBy(baserentalOwnerInvs, [{field: 'creditTotal', aggregate: 'sum'}, 
							{field: 'debitTotal', aggregate: 'sum' }, {field: 'grandTotal', aggregate: 'sum' }]);
					break;
				case ArInvoiceType.CarHireMileageCustomer:
					this.canSendAr = true;
					const carhireInvs = this.selectedArInvoices.filter(x => !x.isPosted);
					this.carhireTotals = aggregateBy(carhireInvs, [{field: 'mileageTotal', aggregate: 'sum'}, 
							{field: 'taxTotal', aggregate: 'sum' }, {field: 'grandTotal', aggregate: 'sum' }]);
					break;
				case ArInvoiceType.ArExcessMileage:
					this.canSendAr = true;
					const exInvs = this.selectedArInvoices.filter(x => !x.isPosted);
					this.arExceedMileageTotals = aggregateBy(exInvs, [{field: 'invoiceLoaded', aggregate: 'sum'}, 
							{field: 'invoiceEmpty', aggregate: 'sum' }, {field: 'invoiceTotalMiles', aggregate: 'sum' },
							{field: 'invoiceLeaseAllowances', aggregate: 'sum' }, {field: 'invoiceExceededMiles', aggregate: 'sum' },
							{field: 'invoicePenaltyAmount', aggregate: 'sum' }
						]);
					
					let keys = Object.keys(this.data).filter(x => x.startsWith('grand'));
					break;	

				case ArInvoiceType.TankcarEqualization:
					this.canSendAr = true;
					const teInvs = this.selectedArInvoices.filter(x => !x.isPosted);
					this.arTankcarEqualizationTotals = aggregateBy(teInvs, 
						[
							{field: 'excessAmount', aggregate: 'sum' }
						]);

					break;	
	
				case ArInvoiceType.Scrap:
					this.canSendAr = true;
					const scrapInvs = this.selectedArInvoices.filter(x => !x.isPosted);
					this.scrapTotals = aggregateBy(scrapInvs, [{field: 'laborCharge', aggregate: 'sum'}, 
							{field: 'materialCharge', aggregate: 'sum' }, {field: 'totalCharge', aggregate: 'sum' }]);
					break;	

				case ArInvoiceType.NoWork:
					this.canSendAr = true;
					const noWorkInvs = this.selectedArInvoices.filter(x => !x.isPosted);
					this.noWorkTotals = aggregateBy(noWorkInvs, [{field: 'laborCharge', aggregate: 'sum'}, 
							{field: 'materialCharge', aggregate: 'sum' }, {field: 'totalCharge', aggregate: 'sum' }]);
					break;	
				
				case ArInvoiceType.StipLoss:
				case ArInvoiceType.FmvDetermination:
					this.canSendAr = true;
					const stipLossFmvInvs = this.selectedArInvoices.filter(x => !x.isPosted);
					this.stipLossFmvTotals = aggregateBy(stipLossFmvInvs, [{field: 'laborCharge', aggregate: 'sum'}, 
							{field: 'materialCharge', aggregate: 'sum' }, {field: 'totalCharge', aggregate: 'sum' }]);
					break;	

				default:			
					this.createArPreview();
					break;
			}
		}

		this.invoiceService.sendArsSequentially(this.invoicesToSendSource, this.arInvType).subscribe(
			(response: string) => {
				this.showIntacctResponseSidePanel = true;
				this.selectedArInvoices[this.totalSentToIntacct].isProcessed = true;
				this.selectedArInvoices[this.totalSentToIntacct].postedDate = new Date(Date.now());
				this.totalSentToIntacct++;

				if (this.totalSentToIntacct === this.totalToSendToIntacct){
					this.arProgressLabel = 'Sending ARs to Intacct complete!'
					this.sendInvoicesInprogress = false;
					this.canSendAr = false;
				} else {
					this.arProgressLabel = `Processing ${this.selectedArInvoices[this.totalSentToIntacct].invoiceNumber} ...`;
				}
				this.intacctResponse.push({ message: response, isError: response.indexOf('successful') == -1});
			},
			error => {
				this.toastr.error(error, `Error occurred for ${this.selectedArInvoices[this.totalSentToIntacct].invoiceNumber}...`);
				this.sendInvoicesInprogress = false;
				this.canSendAr = false;
			}
		);
	}

	get isCustomerRebill() : boolean {
    return this.arInvType === ArInvoiceType.BaseRentalCustomer ||
      this.arInvType === ArInvoiceType.FreightSwitchCustomer ||
      this.arInvType === ArInvoiceType.StorageCustomer ||
      this.arInvType === ArInvoiceType.NoWork;
	}

	get isRepairCustomerAr() : boolean {
		return this.arInvType === ArInvoiceType.RepairCustomer || this.arInvType === ArInvoiceType.RepairCustomerCredit;
	}

	get isOwnerRebill() : boolean {
		return this.arInvType === ArInvoiceType.StorageOwner || this.arInvType === ArInvoiceType.FreightSwitchOwner;
	}

	get isBaseRentalAr(): boolean {
		return this.arInvType === ArInvoiceType.BaseRentalCustomer
			|| this.arInvType === ArInvoiceType.BaseRentalOwner;
	}

	get isBaseRentalOwnerAr(): boolean {
		return this.arInvType === ArInvoiceType.BaseRentalOwner;
	}

	get isCarHireCustomerAr(): boolean {
		return this.arInvType === ArInvoiceType.CarHireMileageCustomer;
	}

	get isExcessMileageAr(): boolean {
		return this.arInvType === ArInvoiceType.ArExcessMileage;
	}

	get isTankcarEqualizationAr(): boolean {
		return this.arInvType === ArInvoiceType.TankcarEqualization;
	}

	get isNoWorkCustomerAr(): boolean {
		return this.arInvType === ArInvoiceType.NoWork;
	}

	get isScrapAr(): boolean {
		return this.arInvType === ArInvoiceType.Scrap;
	}

	get isStipLossAr(): boolean {
		return this.arInvType === ArInvoiceType.StipLoss;
	}

	get isFmvAr(): boolean {
		return this.arInvType === ArInvoiceType.FmvDetermination;
	}

	get isLoadingIntacctData(): boolean {
		return !this.selectedArInvoices.every(x => !x.loadingIntacctData);
	}

	get disableSendToIntacct(): boolean {
		return !this.processingPeriod
			|| this.lockActions
			|| !this.selectedArInvoices.every(x => !!(x.invoiceDate && x.invoiceReceivedDate && x.accountingDueDate && x.glPostingDate));
	}

	public createArPreview() {
    	this.canSendAr = false;
		const toBeSent = this.selectedArInvoices.filter(x => !x.isPosted);
		if (toBeSent.length < 1) {
			this.toastr.warning('There is no non-sent ARs selected!', 'Please select valid AR(s) first.');
			return;
		}

		this.arPreviewArTotal = aggregateBy(toBeSent, [{ field: 'total', aggregate: 'sum' }, { field: 'totalCredit', aggregate: 'sum' },  { field: 'intacctApTotal', aggregate: 'sum' }]);
		this.arPreviewLoading = true;
		this.canClosePannel = false;
		this.invoiceService.createArPreview(toBeSent.map(x => x.id), this.billingPeriod, this.arInvType).subscribe(data => {
			let errCnt = 0;
			let errMsg = '';
			toBeSent.forEach(x => {
				const invoice = data.find(item => item.invoiceNumber === x.invoiceNumber);
				if (invoice) {
					x.intacctApTotal = invoice.accountingApTotal;
					if (x.intacctApTotal != x.total) {
						if (x.payeeCode) {
							x.payeeCode = 'AP/AR amounts not in sync';
						} else {
							x.leaseNumber = 'AP/AR amounts not in sync'; 
						}
						errMsg += x.invoiceNumber + ': AP/AR amounts not in sync\n'
						x.hasError = true;
						errCnt ++;
					}
				} else {
					if (x.payeeCode) {
						x.payeeCode = 'AP missing in Intacct';
					} else {
						x.leaseNumber = 'AP missing in Intacct'; 
					}
					errMsg = errMsg += x.invoiceNumber + ': AP missing in Intacct\n'
					x.hasError = true;
					errCnt ++;
				}
			});

			this.arPreviewArTotal = aggregateBy(toBeSent, [{ field: 'totalCredit', aggregate: 'sum' }, { field: 'total', aggregate: 'sum' }, { field: 'intacctApTotal', aggregate: 'sum' }]);	
			this.arPreviewLoading = false;
			this.canClosePannel = true;
			this.canSendAr = (errMsg === '');
			if (errMsg) {
				this.toastr.warning(errMsg, errCnt + ' Error(s)!');
			}
		});
	}

	public sendArToIntacct() {
		this.intacctResponse = [];
		if(this.selectedArInvoices.length > 0)  {
			this.sendInvoicesInprogress = true;
			this.totalSentToIntacct = 0;
			this.totalToSendToIntacct = this.selectedArInvoices.length;
			this.arProgressLabel = `Processing ${this.selectedArInvoices[this.totalSentToIntacct].invoiceNumber} ...`;
			this.invoicesToSendSource.next(this.selectedArInvoices);
		}
	}

	public closeComponent() {
		this.showSendArPanel = false;
		this.onClosed.emit(this.intacctResponse ? this.intacctResponse.map(x => x.message) : []);
	}

	public updateInvoiceProcessDate(glPostingDate: Date, invoiceNumber: string) {
		this.invoiceService.updateInvoiceDateByNumber(glPostingDate, invoiceNumber).subscribe(data => {
			if (data) {
				this.toastr.success('Successfully saved invoice date for ' + invoiceNumber);
			} else {
				this.toastr.error('No AR Invoice found with number ' + invoiceNumber);
			}
		}, error => {
			this.toastr.error(error);
		});
	}

	toggleIntacctResponseSidePanel() {
		this.showIntacctResponseSidePanel = !this.showIntacctResponseSidePanel;
	}

	public getMarginAboveGrid() {
		return 'calc(40vh - 110px) * .3 / 2';
	}

	public getPreviewGridHeight() {
		if (!this.needPreview) {
			return 'calc(70vh - 30px)';	//no Preview Label
		} else {
			return 'calc(70vh - 85px)';
		}
	}

	get needPreview(): boolean {
		const noPreview = [ArInvoiceType.BaseRentalCustomer, ArInvoiceType.BaseRentalOwner, ArInvoiceType.Scrap, ArInvoiceType.NoWork,
			ArInvoiceType.ArExcessMileage, ArInvoiceType.CarHireMileageCustomer, ArInvoiceType.StipLoss, 
			ArInvoiceType.FmvDetermination, ArInvoiceType.TankcarEqualization]
		return !noPreview.some(x => x === this.arInvType);
	}

	private onNext(result: string) {
		this.sendingRequestToIntacct = false;
		this.intacctResponse.push(result);
		this.toastr.success('See response from Intacct below.', 'Action completed!', {
			timeOut: 3000
		});
	}

	private onError(errorResult) {
		this.sendingRequestToIntacct = false;
	}
}
